// 文件管理相关API服务
import { httpClient } from '../http';
import axios from 'axios';

export interface FileInfo {
  id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  content_type: string;
  md5_hash: string;
  storage_type: 'local' | 's3';
  storage_key: string;
  is_local: boolean;
  created_at: string;
  updated_at: string;
}

export interface UploadResponse {
  id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  content_type: string;
  md5_hash: string;
  storage_type: 'local' | 's3';
  storage_key: string;
  is_local: boolean;
  upload_url?: string;
  created_at: string;
}

export interface PresignedUploadResponse {
  upload_url: string; // 预签名上传URL
  file_path: string; // 文件存储路径
  expires_at: string; // URL过期时间 (ISO 8601格式)
  file_size: number; // 最大文件大小限制
}

export interface ConfirmUploadRequest {
  file_path: string; // 文件路径
  file_size: number; // 文件大小
  md5_hash: string; // MD5哈希值
  content_type?: string; // MIME类型（可选）
}

export class FilesAPI {
  /**
   * 生成预签名上传URL
   * @param fileName 文件名（仅支持.zip、.img、.apk格式）
   */
  async generatePresignedUploadURL(fileName: string): Promise<PresignedUploadResponse> {
    // 设置30秒超时，获取预签名URL应该很快
    const response = await httpClient.get<PresignedUploadResponse>(
      '/admin/files/presigned-upload-url',
      { fileName },
      30000 // 30秒超时
    );
    return response.data;
  }

  /**
   * 上传文件到S3（使用预签名URL）
   * @param uploadURL 预签名上传URL
   * @param file 要上传的文件
   * @param onProgress 上传进度回调函数 (百分比: 0-100)
   */
  async uploadFileToS3(
    uploadURL: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    try {
      // 计算超时时间：根据文件大小动态设置，最小5分钟，最大30分钟
      // 假设上传速度为1MB/s，给足够的缓冲时间
      const fileSizeMB = file.size / (1024 * 1024);
      const timeoutMs = Math.max(5 * 60 * 1000, Math.min(30 * 60 * 1000, fileSizeMB * 2 * 1000));
      
      console.log(`文件大小: ${fileSizeMB.toFixed(2)}MB, 设置超时时间: ${timeoutMs / 1000}秒`);
      
      try {
        // 使用 axios 上传到 S3，支持进度监听
        await axios.put(uploadURL, file, {
          timeout: timeoutMs,
          headers: {
            // 不设置 Content-Type 头，避免触发 CORS 预检请求
            // S3 会自动检测文件类型
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // 计算上传百分比
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              console.log(`上传进度: ${percentCompleted}%`);
              onProgress?.(percentCompleted);
            }
          },
        });

        console.log('文件上传到S3成功');
      } catch (axiosError: any) {
        if (axiosError.code === 'ECONNABORTED') {
          throw new Error(`上传超时：文件较大，上传时间超过${timeoutMs / 1000}秒，请检查网络连接后重试`);
        }
        
        if (axiosError.response) {
          // 服务器返回了错误响应
          const errorText = axiosError.response.data || axiosError.response.statusText;
          throw new Error(`上传文件到S3失败 (${axiosError.response.status}): ${errorText}`);
        }
        
        throw new Error(`上传文件到S3失败: ${axiosError.message || '网络错误'}`);
      }
    } catch (error: any) {
      console.error('上传文件到S3失败:', error);
      throw error;
    }
  }

  /**
   * 确认上传完成
   * @param request 确认上传请求
   */
  async confirmUpload(request: ConfirmUploadRequest): Promise<FileInfo> {
    // 设置60秒超时，确认上传可能需要一些时间
    const response = await httpClient.post<FileInfo>('/admin/files/confirm-upload', request, undefined, {
      timeout: 60000 // 60秒超时
    });
    return response.data;
  }

  /**
   * 计算文件的MD5哈希值
   * 注意：需要安装 spark-md5 库: npm install spark-md5 @types/spark-md5
   * @param file 文件对象
   */
  private async calculateMD5(file: File): Promise<string> {
    try {
      // 尝试动态导入 spark-md5
      const SparkMD5 = (await import('spark-md5')).default;
      return new Promise<string>((resolve, reject) => {
        const blobSlice = File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice;
        const chunkSize = 2097152; // 2MB chunks
        const chunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        const spark = new SparkMD5.ArrayBuffer();
        const fileReader = new FileReader();

        fileReader.onload = function (e) {
          if (e.target?.result) {
            spark.append(e.target.result as ArrayBuffer);
            currentChunk++;

            if (currentChunk < chunks) {
              loadNext();
            } else {
              resolve(spark.end());
            }
          }
        };

        fileReader.onerror = function () {
          reject(new Error('文件读取失败'));
        };

        function loadNext() {
          const start = currentChunk * chunkSize;
          const end = start + chunkSize >= file.size ? file.size : start + chunkSize;
          fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
        }

        loadNext();
      });
    } catch (error) {
      // 如果 spark-md5 未安装，抛出错误提示用户
      throw new Error(
        'MD5计算失败：请先安装 spark-md5 库。运行命令: npm install spark-md5 @types/spark-md5'
      );
    }
  }

  /**
   * 上传文件流程：获取预签名URL -> PUT上传到S3 -> 确认上传
   * @param file 要上传的文件
   * @param onProgress 上传进度回调函数 (百分比: 0-100)
   */
  async uploadFile(file: File, onProgress?: (progress: number) => void): Promise<FileInfo> {
    try {
      console.log('开始上传文件:', file.name, '大小:', file.size);
      
      // 1. 获取预签名上传URL
      console.log('步骤1: 获取预签名上传URL...');
      onProgress?.(5); // 5% - 获取预签名URL
      const presignedResponse = await this.generatePresignedUploadURL(file.name);
      console.log('预签名URL获取成功:', presignedResponse);

      // 2. 计算文件的MD5哈希值
      console.log('步骤2: 计算文件MD5哈希值...');
      onProgress?.(10); // 10% - 开始计算MD5
      const md5Hash = await this.calculateMD5(file);
      console.log('MD5计算完成:', md5Hash);
      onProgress?.(15); // 15% - MD5计算完成

      // 3. 上传文件到S3 (15% - 95%)
      console.log('步骤3: 上传文件到S3...');
      await this.uploadFileToS3(presignedResponse.upload_url, file, (uploadProgress) => {
        // 将上传进度映射到 15% - 95% 区间
        const mappedProgress = 15 + (uploadProgress * 0.8);
        onProgress?.(Math.round(mappedProgress));
      });
      console.log('文件上传到S3成功');
      onProgress?.(95); // 95% - S3上传完成

      // 4. 确认上传完成
      console.log('步骤4: 确认上传完成...');
      const confirmRequest: ConfirmUploadRequest = {
        file_path: presignedResponse.file_path,
        file_size: file.size,
        md5_hash: md5Hash,
        content_type: file.type || undefined,
      };

      const result = await this.confirmUpload(confirmRequest);
      console.log('上传完成，文件信息:', result);
      onProgress?.(100); // 100% - 完成
      return result;
    } catch (error: any) {
      console.error('文件上传失败:', error);
      throw new Error(`文件上传失败: ${error.message || error}`);
    }
  }

  // 获取文件信息
  async getFileById(id: number): Promise<FileInfo> {
    const response = await httpClient.get<FileInfo>(`/admin/files/${id}`);
    return response.data;
  }

  // 获取文件访问URL
  async getFileURL(id: number): Promise<string> {
    const response = await httpClient.get<{ url: string }>(`/admin/files/${id}/url`);
    return response.data.url;
  }

  // 生成预签名下载URL
  async generatePresignedDownloadURL(id: number): Promise<string> {
    const response = await httpClient.get<{ download_url: string }>(
      `/admin/files/${id}/presigned-download-url`
    );
    return response.data.download_url;
  }

  // 删除文件
  async deleteFile(id: number): Promise<void> {
    await httpClient.delete(`/admin/files/${id}`);
  }

  // 获取文件列表
  async getFiles(params?: {
    page?: number;
    size?: number;
    storage_type?: 'local' | 's3';
  }): Promise<{ list: FileInfo[]; total: number; page: number; size: number }> {
    const response = await httpClient.get<{
      list: FileInfo[];
      total: number;
      page: number;
      size: number;
    }>('/admin/files', params);
    return response.data;
  }
}

// 导出单例实例
export const filesAPI = new FilesAPI();

